import React, { useState, useEffect } from 'react'
import { Group, Modal, Select, TextInput, Text, Checkbox } from '@mantine/core'
import { HolidayCalendarInfoParams, HolidayCalendarParams } from 'app/models/holiday-calendar-params'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import { useForm } from '@mantine/form'
import { calendarYears } from 'app/models/dropdown-options'
import { DatePickerInput } from '@mantine/dates';
import { activateCalendar, saveCalendar } from 'app/state/ducks/holiday-calendars/thunks'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { useDispatch, useSelector } from 'react-redux'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { formatDateToUTC } from 'app/utils/util-functions'
import SecondaryButton from 'app/views/components/buttons/SecondaryButton'
import MainTable from 'app/views/components/Table/MainTable'
import { getHolidaySummaryColumnDefs } from './holiday-summary-columnDefs'
import CustomHoverCard from 'app/views/components/popover/custom-hover-card'
import { ReactComponent as InfoIcon } from '../../../public/generic-icons/info-icon.svg'
import { ReactComponent as AddIcon } from '../../../public/generic-icons/add-icon.svg'

type HolidayCalendarModalProps = {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  open: boolean
  holidayCalendar?: HolidayCalendarParams
  updateCalendar: (calendar: HolidayCalendarParams) => void
}

export const HolidayCalendarModal: React.FC<HolidayCalendarModalProps> = ({
  setOpen,
  open,
  holidayCalendar,
  updateCalendar,
}) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [calendarSelection, setCalendarSelection] = useState<Date[]>([]);
  const [holidayInfo, setHolidayInfo] = useState<HolidayCalendarInfoParams[] | []>(holidayCalendar?.holidayInfo ?? []);
  const businessDate: Date = useSelector(getBusinessDate)
  const [date, setDate] = useState(businessDate);

  useEffect(() => {
    if (!holidayCalendar) {
      form.reset();
      setHolidayInfo([]);
      setCalendarSelection([]);
    } else {
      form.setValues({
        name: holidayCalendar.name,
        year: holidayCalendar.year,
        holidayName: '',
        lstaTradeCalcs: holidayCalendar.lstaTradeCalcs,
        lmaTradeCalcs: holidayCalendar.lmaTradeCalcs,
        systemDate: holidayCalendar.systemDate,
        status: holidayCalendar.status ?? 'inactive'
      });
      setHolidayInfo(holidayCalendar.holidayInfo ?? []);
    }
  }, [holidayCalendar]);

  const form: any = useForm({
    initialValues: {
      name: holidayCalendar?.name ?? '',
      year: holidayCalendar?.year ?? '',
      holidayName: '',
      lstaTradeCalcs: holidayCalendar?.lstaTradeCalcs ?? false,
      lmaTradeCalcs: holidayCalendar?.lmaTradeCalcs ?? false,
      systemDate: holidayCalendar?.systemDate ?? false,
      status: holidayCalendar?.status ?? 'inactive'
    },
    validate: {
      name: value => (value === '' ? 'Required' : null),
      year: value => (value === '' ? 'Required' : null),
    },
    transformValues: values => ({
      name: values.name,
      year: values.year,
      lstaTradeCalcs: values.lstaTradeCalcs,
      lmaTradeCalcs: values.lmaTradeCalcs,
      systemDate: values.systemDate,
      holidayInfo: holidayInfo,
      status: values.status,
    }),
    validateInputOnBlur: true,
  })

  const addHolidayToList = () => {
    const holidayName = form.values.holidayName
    const holidayDates = calendarSelection
    const holidays: HolidayCalendarInfoParams[] = []
    if (holidayName && holidayDates.length > 0) {
      holidayDates.forEach((date: Date) => {
        const holiday = {
          holidayName: holidayName,
          holidayDate: formatDateToUTC(date),
        }
        holidays.push(holiday)
      })
      const combinedHolidays = [...holidayInfo, ...holidays];
      combinedHolidays.sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
      setHolidayInfo(combinedHolidays)
      setCalendarSelection([])
      form.values.holidayName = ''
    }
  }

  const submitCalendar = async (holidayCalendar: HolidayCalendarParams, oldHolidayCalendar?: HolidayCalendarParams) => {
    const response: any = await dispatch(saveCalendar(holidayCalendar, oldHolidayCalendar))
    if (response?.success) {
      SuccessNotification({
        title: 'Saved Holiday Calendar',
      })
      updateCalendar(response.payload.data)
    } else {
      ErrorNotification({
        title: 'Holiday Calendar Failed to Save',
        message: response.payload ?? 'Check data and try again',
      })
    }
    return response
  }

  async function submitForm(event: { preventDefault: () => void }) {
    event.preventDefault()
    setIsLoading(true)
    try {
      await submitCalendar(form.getTransformedValues(), holidayCalendar)
      setIsLoading(false)
      setOpen(false)
    } catch {
      ErrorNotification({
        title: 'Holiday Calendar Failed to Save',
        message: 'Check data and try again',
      })
      setIsLoading(false)
    }
  }

  const submitAndActivate = async () => {
    setIsLoading(true)
    const response: any = await submitCalendar(form.getTransformedValues(), holidayCalendar)
    try {
      if (!response.success) {
        return
      }
      const result: any = await dispatch(
        activateCalendar(response.payload.data)
      )
      if (result.success) {
        SuccessNotification({
          title: 'Activated Holiday Calendar',
        })
        setOpen(false)
        setIsLoading(false)
      } else {
        ErrorNotification({
          title: 'Holiday Calendar Failed to activate',
          message: result.payload ?? 'Check data and try again',
        })
        setIsLoading(false)
      }
    } catch {
      ErrorNotification({
        title: 'Holiday Calendar Failed to activate',
        message: 'Check data and try again',
      })
      setIsLoading(false)
    }
  }

  const yearChange = (year: string) => {
    form.setFieldValue('year', year)
    const curMonth = businessDate.getMonth()
    setDate(() => new Date(Number(year), curMonth));
  }


  const handleDeleteHoliday: any = (deletedHoliday: { holidayName: string; holidayDate: string }) => {
    setHolidayInfo((prev) =>
      prev.filter(
        (row) =>
          row.holidayName !== deletedHoliday.holidayName ||
          row.holidayDate !== deletedHoliday.holidayDate
      )
    );
  };


  return (
    <Modal
      className="modal-body create-new-form fit-content-modal"
      opened={open}
      onClose={() => setOpen(!open)}
      centered={true}
      size="80%"
    >
      <div style={{ padding: "40px" }}>
        <FormWrapper
          title={`${holidayCalendar ? 'Edit' : 'Add'} Holiday Calendar`}>
          <div className="content">
            <form onSubmit={submitForm}>
              <div className="create-new holiday-modal-body" >
                <div className="holiday-input-block">
                  <Group noWrap w="100%">
                    <TextInput
                      classNames={{ input: 'holidayDataPicker', root: 'width-100', label: 'holidayLabel' }}
                      label="Calendar Name"
                      name="Calendar Name"
                      id="name"
                      placeholder="Enter Calendar Name"
                      {...form.getInputProps('name')}
                    />
                  </Group>
                  <Group noWrap w="100%">
                    <Select
                      classNames={{ input: 'holidayDataPicker', root: 'width-100' }}
                      label="Calendar Year"
                      placeholder="Select Calendar Year"
                      data={calendarYears}

                      onChange={(event: string) => {
                        if (event) {
                          yearChange(event)
                        }
                      }}
                      value={form.values.year}
                    />
                  </Group>
                  <div className='holidayCalendarDataPicker'>
                    <Text className='holidaySelectorLabel'>Select Dates</Text>
                    <DatePickerInput
                      classNames={{ input: 'holidayDataPicker', root: 'width-100' }}
                      valueFormat='DD-MMM-YYYY'
                      type="multiple"
                      firstDayOfWeek={0}
                      date={date}
                      value={calendarSelection}
                      onChange={setCalendarSelection}
                      onDateChange={setDate}
                    />

                  </div>
                  <Group noWrap w="100%">
                    <TextInput
                      classNames={{ input: 'holidayDataPicker', root: 'width-100' }}
                      label="Holiday Name"
                      name="Holiday Name"
                      id="holidayName"
                      placeholder="Holiday Name"
                      {...form.getInputProps('holidayName')}
                    />
                  </Group>
                  <Group noWrap w="fit-content"
                    onClick={() => addHolidayToList()}
                    style={{
                      cursor: "pointer"
                    }}>
                    <AddIcon style={{ width: "30px", height: "30px" }} />
                    <Text w='100%' className='holidayFormAdd'>
                      Add Holiday
                    </Text>
                  </Group>
                </div>
                <div className='holidayCalendarFormSummary'>
                  <div className='holidayCalendarCheckList'>
                    <Group noWrap>
                      <Checkbox
                        mb='5px'
                        checked={form.values.lstaTradeCalcs}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          form.setFieldValue('lstaTradeCalcs', event.currentTarget.checked)
                        }
                      />
                      <div className='cost-of-carry-label'>
                        LSTA Trade Calculations
                        <CustomHoverCard
                          cardTitle='Info'
                          position='bottom'
                          targetComponent={
                            <InfoIcon style={{ width: '1.5em' }} />
                          }
                        >
                          <Text className='hover-card-alert-text'>
                            When selected, this holiday calendar will be used to adjust dates related to trading, including trade and settlement dates.
                          </Text>
                        </CustomHoverCard>
                      </div>
                    </Group>
                    <Group noWrap>
                      <Checkbox
                        mb='5px'
                        checked={form.values.lmaTradeCalcs}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          form.setFieldValue('lmaTradeCalcs', event.currentTarget.checked)
                        }
                      />
                      <div className='cost-of-carry-label'>
                        LMA Trade Calculations
                        <CustomHoverCard
                          cardTitle='Info'
                          position='bottom'
                          targetComponent={
                            <InfoIcon style={{ width: '1.5em' }} />
                          }
                        >
                          <Text className='hover-card-alert-text'>
                            When selected, this holiday calendar will be used to adjust dates related to trading, including trade and settlement dates.
                          </Text>
                        </CustomHoverCard>
                      </div>
                    </Group>
                    <Group noWrap>
                      <Checkbox
                        mb='5px'
                        checked={form.values.systemDate}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          form.setFieldValue('systemDate', event.currentTarget.checked)
                        }
                      />
                      <div className='cost-of-carry-label'>
                        System Date
                        <CustomHoverCard
                          cardTitle='Info'
                          position='bottom'
                          targetComponent={
                            <InfoIcon style={{ width: '1.5em' }} />
                          }
                        >
                          <Text className='hover-card-alert-text'>
                            When selected, this calendar dictates the system's working date, which impacts scheduled processes, reporting, and other system-wide date operations.
                          </Text>
                        </CustomHoverCard>
                      </div>
                    </Group>

                  </div>
                  {/* <div className="divider"></div> */}
                  <Text className='holidayCalendarFormSummaryTitle'>
                    Summary
                  </Text>
                  <MainTable
                    tableName="Holiday Calendar Summary"
                    stretch={true}
                    maxHeight='200px'
                    columnDefs={getHolidaySummaryColumnDefs(handleDeleteHoliday)}
                    data={holidayInfo}
                    setRow={() => { }}
                    headerBackgroundColor="#F0EEEE"
                    enableColumnActions={false}
                    enableTopToolbar={false}
                    withPagination={false}
                    csvExportEnabled={false}
                    enableColumnResizing={false}
                    enableColumnOrdering={false}
                    enableFilters={false}
                    enableSorting={false}
                    enableGrouping={false}
                    minHeight="0px"
                    isLoading={false}
                    border={true}
                  />
                  <div
                    style={{
                      display: 'flex',
                      gap: '.5rem',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <SecondaryButton
                      variant="outline"
                      className="form-button"
                      loading={isLoading}
                      w="100%"
                      onClick={() => {
                        setOpen(false)
                      }}
                    >
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton
                      disabled={!form.isValid()}
                      className="form-button"
                      loading={isLoading}
                      type="submit"
                      w="100%"
                    >
                      Submit
                    </PrimaryButton>
                    <PrimaryButton
                      disabled={!form.isValid()}
                      className="form-button"
                      loading={isLoading}
                      miw="fit-content"
                      onClick={() => submitAndActivate()}
                    >
                      Submit and Activate
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </FormWrapper >
      </div>
    </Modal >
  )
}
